/**
 * T083 — Tenant picker (FR-043d).
 *
 * Landing page for users with multiple tenant mappings. Redirects straight
 * to /expenses if only one tenant is available.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { Button, PageHeader } from '../components/ui';
import { useSession, useSelectTenant } from '../hooks/useTenantContext';

export const Route = createFileRoute('/tenant-picker')({
  component: TenantPickerPage,
});

function TenantPickerPage() {
  return (
    <ProtectedRoute>
      <TenantPickerInner />
    </ProtectedRoute>
  );
}

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  geschäftsführer: 'Geschäftsführer',
  steuerberater: 'Steuerberater',
};

function TenantPickerInner() {
  const { data, isLoading } = useSession();
  const select = useSelectTenant();
  const navigate = useNavigate();

  // Auto-redirect single-tenant users.
  useEffect(() => {
    if (data && data.availableTenants.length === 1 && data.activeTenantId) {
      navigate({ to: '/expenses' });
    }
  }, [data, navigate]);

  async function onPick(tenantId: string) {
    try {
      await select.mutateAsync(tenantId);
      navigate({ to: '/expenses' });
    } catch {
      // useSelectTenant already invalidates; error surfaces in UI below
    }
  }

  if (isLoading) {
    return <div className="p-8 text-white/50">Lade…</div>;
  }
  if (!data) {
    return <div className="p-8 text-red-400">Keine Session gefunden.</div>;
  }
  if (data.availableTenants.length === 0) {
    return (
      <div className="max-w-md mx-auto p-8 mt-20 text-center">
        <PageHeader
          title="Kein Zugang"
          subtitle="Dein Konto ist keinem Mandanten zugeordnet. Bitte wende dich an einen Administrator."
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-8 mt-16">
      <PageHeader
        title="Mandant wählen"
        subtitle={
          <>
            Du hast Zugriff auf {data.availableTenants.length} Mandanten. Wähle, in welchem
            Kontext du arbeiten möchtest.
          </>
        }
      />
      <div className="space-y-3">
        {data.availableTenants.map((t) => (
          <button
            key={t.tenantId}
            onClick={() => onPick(t.tenantId)}
            className="w-full flex justify-between items-center p-4 bg-white/5 border border-white/10 rounded hover:bg-white/10 transition text-left"
            disabled={select.isPending}
          >
            <div>
              <div className="text-white font-medium">{t.name}</div>
              <div className="text-white/50 text-sm">
                Rolle: {ROLE_LABEL[t.role] ?? t.role}
                {t.tenantId === data.activeTenantId ? ' · aktiv' : ''}
              </div>
            </div>
            <Button variant="ghost">Wählen →</Button>
          </button>
        ))}
      </div>
    </div>
  );
}
